/**
 * Capture tokens for quick-save (the iOS Shortcut). The token is made here,
 * shown once, and only its SHA-256 hash is stored (table capture_tokens).
 */
import { supabase } from '@/integrations/supabase/client';
import { CAPTURE_TOKEN_PREFIX } from '../../supabase/functions/_shared/quickCapture';

export interface CaptureTokenRow {
  id: string;
  name: string;
  token_prefix: string;
  created_at: string;
  last_used_at: string | null;
}

/** mf_ + 32 random bytes, base64url: 46 characters, unguessable. */
export function generateCaptureToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const b64 = btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return `${CAPTURE_TOKEN_PREFIX}${b64}`;
}

export async function hashCaptureToken(token: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** The URL the Shortcut posts to. `reply=text` makes the answer a plain sentence for its notification. */
export function quickSaveUrl(): string {
  return `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/quick-save?reply=text`;
}

export async function listCaptureTokens(): Promise<CaptureTokenRow[]> {
  const { data, error } = await supabase
    .from('capture_tokens')
    .select('id, name, token_prefix, created_at, last_used_at')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

/** Creates a token and returns it in full. This is the only time the full value exists outside the phone. */
export async function createCaptureToken(name = 'iPhone Shortcut'): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Sign in first');
  const token = generateCaptureToken();
  const { error } = await supabase.from('capture_tokens').insert({
    user_id: user.id,
    name,
    token_hash: await hashCaptureToken(token),
    token_prefix: token.slice(0, 10),
  });
  if (error) throw error;
  return token;
}

export async function revokeCaptureToken(id: string): Promise<void> {
  const { error } = await supabase.from('capture_tokens').delete().eq('id', id);
  if (error) throw error;
}
